import { Link, router } from '@inertiajs/react'
import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'

export interface PageNode {
  id: number
  title: string
  slug: string
  path: string
  is_folder: boolean
  children: PageNode[]
}

// Persists across Inertia navigations (module-level, not component state)
const expandedOverrides = new Map<number, boolean>()

function findAncestorIds(pages: PageNode[], activeSlug: string | undefined): Set<number> {
  const result = new Set<number>()
  if (!activeSlug) return result

  function walk(nodes: PageNode[], path: number[]): boolean {
    for (const node of nodes) {
      if (node.slug === activeSlug) {
        for (const id of path) result.add(id)
        return true
      }
      if (node.children.length > 0 && walk(node.children, [...path, node.id])) {
        return true
      }
    }
    return false
  }

  walk(pages, [])
  return result
}

function buildFlatMap(pages: PageNode[]): Map<number, PageNode> {
  const map = new Map<number, PageNode>()
  function walk(nodes: PageNode[]) {
    for (const node of nodes) {
      map.set(node.id, node)
      if (node.children.length > 0) walk(node.children)
    }
  }
  walk(pages)
  return map
}

function isDescendantOf(flatMap: Map<number, PageNode>, potentialAncestorId: number, targetId: number): boolean {
  const ancestor = flatMap.get(potentialAncestorId)
  if (!ancestor) return false

  function walk(nodes: PageNode[]): boolean {
    for (const node of nodes) {
      if (node.id === targetId) return true
      if (node.children.length > 0 && walk(node.children)) return true
    }
    return false
  }

  return walk(ancestor.children)
}

function findParentId(pages: PageNode[], targetId: number): number | null {
  function walk(nodes: PageNode[], parentId: number | null): number | null {
    for (const node of nodes) {
      if (node.id === targetId) return parentId
      if (node.children.length > 0) {
        const found = walk(node.children, node.id)
        if (found !== undefined) return found
      }
    }
    return undefined as unknown as number | null
  }
  const result = walk(pages, null)
  return result === undefined ? null : result
}

interface PageTreeProps {
  pages: PageNode[]
  workspaceSlug: string
  activePageSlug?: string
  userRole?: 'admin' | 'editor' | 'viewer'
}

interface DraggablePageItemProps {
  page: PageNode
  workspaceSlug: string
  activePageSlug?: string
  depth: number
  ancestorIds: Set<number>
  activeDragId: number | null
  hovered: number | null
  onHover: (id: number | null) => void
  renamingId: number | null
  onCreate: (parentId: number | null, isFolder: boolean) => void
  onRename: (pageId: number, pagePath: string, newTitle: string) => void
  onCancelRename: () => void
  userRole?: 'admin' | 'editor' | 'viewer'
}

interface PageItemMenuProps {
  page: PageNode
  workspaceSlug: string
  isFolder: boolean
  visible: boolean
  onCreate: (parentId: number | null, isFolder: boolean) => void
  userRole?: 'admin' | 'editor' | 'viewer'
}

function PageItemMenu({ page, workspaceSlug, isFolder, visible, onCreate, userRole }: PageItemMenuProps) {
  const isViewer = userRole === 'viewer'
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const dropRef = useRef<HTMLDivElement>(null)
  const [style, setStyle] = useState<React.CSSProperties>({})

  useEffect(() => {
    if (!open) return

    function handleClick(e: MouseEvent) {
      if (
        dropRef.current && !dropRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  function handleToggle() {
    if (open) { setOpen(false); return }
    const rect = btnRef.current?.getBoundingClientRect()
    if (rect) {
      setStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        right: document.documentElement.clientWidth - rect.right,
        zIndex: 9999,
      })
    }
    setOpen(true)
  }

  function handleDelete() {
    setOpen(false)
    if (window.confirm(`Delete "${page.title}"?`)) {
      const currentPath = window.location.pathname
      const deletedPath = `/${workspaceSlug}/${page.path}`
      const isViewingDeleted = currentPath === deletedPath || currentPath.startsWith(deletedPath + '/')
      const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
      fetch(`/${workspaceSlug}/-/pages/${page.id}`, {
        method: 'DELETE',
        headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrf },
      }).then((res) => {
        if (!res.ok) return
        if (isViewingDeleted) {
          router.visit(`/${workspaceSlug}`)
        } else {
          router.reload()
        }
      })
    }
  }

  const dropdown = open && (
    <div
      ref={dropRef}
      className="w-40 rounded-md bg-white py-1 shadow-lg ring-1 ring-gray-200"
      style={style}
    >
      {isFolder && (
        <>
          <button
            className="block w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
            onClick={() => {
              setOpen(false)
              if (isViewer) {
                router.visit(`/${workspaceSlug}/-/new?parent_id=${page.id}`)
              } else {
                onCreate(page.id, false)
              }
            }}
          >
            Add page
          </button>
          {!isViewer && (
            <button
              className="block w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => { setOpen(false); onCreate(page.id, true) }}
            >
              Add folder
            </button>
          )}
          {!isViewer && (
            <button
              className="block w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => { setOpen(false); router.visit(`/${workspaceSlug}/${page.path}`) }}
            >
              Edit
            </button>
          )}
        </>
      )}
      {!isViewer && (
        <button
          className="block w-full px-3 py-1.5 text-left text-sm text-red-600 hover:bg-gray-100"
          onClick={handleDelete}
        >
          Delete
        </button>
      )}
    </div>
  )

  // Viewer only has actions on folders (Add page); hide menu entirely for non-folders
  if (isViewer && !isFolder) return null

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleToggle}
        onPointerDown={(e) => e.stopPropagation()}
        className="mr-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded hover:bg-gray-200"
        style={{ visibility: open || visible ? 'visible' : 'hidden' }}
        aria-label="Page actions"
      >
        <svg className="h-4 w-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
          <circle cx="10" cy="4" r="1.5" />
          <circle cx="10" cy="10" r="1.5" />
          <circle cx="10" cy="16" r="1.5" />
        </svg>
      </button>
      {dropdown && createPortal(dropdown, document.body)}
    </>
  )
}

function DraggablePageItem({ page, workspaceSlug, activePageSlug, depth, ancestorIds, activeDragId, hovered, onHover, renamingId, onCreate, onRename, onCancelRename, userRole }: DraggablePageItemProps) {
  const hasChildren = page.children.length > 0
  const isFolder = page.is_folder
  const isActive = page.slug === activePageSlug
  const escapePressedRef = useRef(false)

  const defaultExpanded = ancestorIds.has(page.id)
  const [expanded, setExpanded] = useState(
    expandedOverrides.has(page.id) ? expandedOverrides.get(page.id)! : defaultExpanded
  )

  useEffect(() => {
    if (ancestorIds.has(page.id)) {
      setExpanded(true)
      expandedOverrides.set(page.id, true)
    }
  }, [ancestorIds, page.id])

  const handleToggle = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev
      expandedOverrides.set(page.id, next)
      return next
    })
  }, [page.id])

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({ id: page.id, disabled: userRole === 'viewer' })

  const {
    setNodeRef: setDropRef,
    isOver,
  } = useDroppable({
    id: `drop-${page.id}`,
  })

  const isDropTarget = isOver && activeDragId !== page.id

  return (
    <li ref={setDropRef}>
      <div
        ref={setDragRef}
        {...attributes}
        {...listeners}
        onMouseEnter={() => onHover(page.id)}
        onMouseLeave={() => onHover(null)}
        className={`flex items-center rounded-md text-sm ${
          isDragging
            ? 'opacity-50'
            : isDropTarget
              ? 'bg-indigo-50 ring-1 ring-indigo-200'
              : isActive
                ? 'bg-indigo-50 text-indigo-700 font-medium'
                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
        }`}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
      >
        {isFolder ? (
          <button
            onClick={handleToggle}
            onPointerDown={(e) => e.stopPropagation()}
            className="mr-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded hover:bg-gray-200"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            <svg
              className={`h-3 w-3 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        ) : (
          <span className="mr-1 w-5 flex-shrink-0" />
        )}
        {renamingId === page.id ? (
          <input
            defaultValue={page.title}
            autoFocus
            onFocus={(e) => e.target.select()}
            onBlur={(e) => {
              if (escapePressedRef.current) {
                escapePressedRef.current = false
                onCancelRename()
              } else {
                onRename(page.id, page.path, e.target.value)
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
              if (e.key === 'Escape') { escapePressedRef.current = true; (e.target as HTMLInputElement).blur() }
            }}
            className="flex-1 truncate py-1 px-1 text-sm bg-white border border-indigo-300 rounded outline-none"
          />
        ) : isFolder ? (
          <button
            onClick={handleToggle}
            className="block flex-1 truncate py-1.5 text-left"
          >
            {page.title}
          </button>
        ) : (
          <Link
            href={`/${workspaceSlug}/${page.path}`}
            className="block flex-1 truncate py-1.5"
          >
            {page.title}
          </Link>
        )}
        <PageItemMenu page={page} workspaceSlug={workspaceSlug} isFolder={isFolder} visible={hovered === page.id} onCreate={onCreate} userRole={userRole} />
      </div>
      {hasChildren && expanded && (
        <ul>
          {page.children.map((child) => (
            <DraggablePageItem
              key={child.id}
              page={child}
              workspaceSlug={workspaceSlug}
              activePageSlug={activePageSlug}
              depth={depth + 1}
              ancestorIds={ancestorIds}
              activeDragId={activeDragId}
              hovered={hovered}
              onHover={onHover}
              renamingId={renamingId}
              onCreate={onCreate}
              onRename={onRename}
              onCancelRename={onCancelRename}
              userRole={userRole}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

function RootDropZone({ activeDragId, children }: { activeDragId: number | null; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'drop-root' })
  return (
    <div
      ref={setNodeRef}
      className={`flex flex-1 flex-col rounded-md transition-colors ${
        isOver && activeDragId ? 'bg-indigo-50 ring-1 ring-indigo-200' : ''
      }`}
    >
      <div style={{ height: 10, flexShrink: 0 }} />
      {children}
    </div>
  )
}

export default function PageTree({ pages, workspaceSlug, activePageSlug, userRole }: PageTreeProps) {
  const [activeDragId, setActiveDragId] = useState<number | null>(null)
  const [hoveredId, setHoveredId] = useState<number | null>(null)
  const [renamingId, setRenamingId] = useState<number | null>(null)
  const renamingIsFolderRef = useRef(false)

  const handleCreate = useCallback(async (parentId: number | null, isFolder: boolean) => {
    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
    const res = await fetch(`/${workspaceSlug}/-/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': csrf },
      body: JSON.stringify({ page: { title: 'Untitled', content_md: '', parent_id: parentId, is_folder: isFolder } }),
    })
    if (!res.ok) return
    const data = await res.json()
    if (parentId !== null) expandedOverrides.set(parentId, true)
    renamingIsFolderRef.current = isFolder
    setRenamingId(data.id)
    router.reload()
  }, [workspaceSlug])

  const handleRename = useCallback(async (pageId: number, pagePath: string, newTitle: string) => {
    const wasFolder = renamingIsFolderRef.current
    setRenamingId(null)
    const title = newTitle.trim() || 'Untitled'
    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
    const res = await fetch(`/${workspaceSlug}/-/pages/${pageId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': csrf },
      body: JSON.stringify({ page: { title } }),
    })
    if (wasFolder) {
      router.reload()
    } else {
      const data = res.ok ? await res.json() : null
      router.visit(`/${workspaceSlug}/${data?.path ?? pagePath}`)
    }
  }, [workspaceSlug])

  const handleCancelRename = useCallback(() => {
    setRenamingId(null)
  }, [])

  const ancestorIds = useMemo(
    () => findAncestorIds(pages, activePageSlug),
    [pages, activePageSlug],
  )

  const flatMap = useMemo(() => buildFlatMap(pages), [pages])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as number)
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDragId(null)
      const { active, over } = event
      if (!over) return

      const pageId = active.id as number
      const overId = String(over.id)

      let newParentId: number | null = null
      if (overId === 'drop-root') {
        newParentId = null
      } else if (overId.startsWith('drop-')) {
        const targetId = parseInt(overId.replace('drop-', ''), 10)
        const targetNode = flatMap.get(targetId)
        if (!targetNode) return
        newParentId = targetNode.is_folder ? targetId : findParentId(pages, targetId)
      } else {
        return
      }

      if (newParentId === pageId) return
      const currentParentId = findParentId(pages, pageId)
      if (currentParentId === newParentId) return
      if (newParentId !== null && isDescendantOf(flatMap, pageId, newParentId)) return

      router.patch(
        `/${workspaceSlug}/-/move`,
        { page_id: pageId, parent_id: newParentId },
        { preserveScroll: true },
      )
    },
    [pages, flatMap, workspaceSlug],
  )

  const handleDragCancel = useCallback(() => {
    setActiveDragId(null)
  }, [])

  const activePage = activeDragId ? flatMap.get(activeDragId) : null

  const isViewer = userRole === 'viewer'

  const createButtons = (
    <div className="flex items-center gap-1 px-3 py-2">
      <button
        onClick={() => {
          if (isViewer) {
            router.visit(`/${workspaceSlug}/-/new`)
          } else {
            handleCreate(null, false)
          }
        }}
        className="flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        aria-label="New page"
        title="New page"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      </button>
      {!isViewer && (
        <button
          onClick={() => handleCreate(null, true)}
          className="flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          aria-label="New folder"
          title="New folder"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 10.5v6m3-3H9m4.06-7.19l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
          </svg>
        </button>
      )}
    </div>
  )

  if (pages.length === 0) {
    return (
      <>
        <div className="px-3 py-2 text-sm text-gray-400">
          No pages yet
        </div>
        {createButtons}
      </>
    )
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <RootDropZone activeDragId={activeDragId}>
        <nav aria-label="Page tree">
          <ul className="space-y-0.5">
            {pages.map((page) => (
              <DraggablePageItem
                key={page.id}
                page={page}
                workspaceSlug={workspaceSlug}
                activePageSlug={activePageSlug}
                depth={0}
                ancestorIds={ancestorIds}
                activeDragId={activeDragId}
                hovered={hoveredId}
                onHover={setHoveredId}
                renamingId={renamingId}
                onCreate={handleCreate}
                onRename={handleRename}
                onCancelRename={handleCancelRename}
                userRole={userRole}
              />
            ))}
          </ul>
          {createButtons}
        </nav>
      </RootDropZone>
      <DragOverlay>
        {activePage ? (
          <div className="rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-md ring-1 ring-gray-200">
            {activePage.title}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
